/**
 * kult-b2b-lead.js — Versand der Firmenkunden-Formulare über die Kult-App plus Einwilligungs-Häkchen.
 * Vertrag: docs/requirements/2026-10-04-b2b-leads-spec.md (§3 Endpunkt, §7 Theme).
 *
 * Seit Okt. 2026 der EINZIGE Versandweg der Firmenkunden-Formulare: Die Angaben gehen als JSON per POST an die App
 * (Standard /apps/seminare/b2b-anfrage). Die App speichert die Anfrage (Firmenanfragen) und schickt die Anfrage-Mail an die
 * Shop-Adresse (Antwortadresse = anfragende Person). Das Shopify-Kontaktformular (POST /contact) wird nicht mehr benutzt:
 * Es lief je nach Bot-Schutz auf eine weiße Prüfseite. Kein Seitenwechsel; Erfolg und Fehler zeigt das jeweilige Formular.
 *
 * Aufruf:  window.KultB2BLead.submit(form) → Promise<{ ok, status, error }>   (wirft nie; 20 s Zeitlimit je Versuch; je
 *          Formular läuft höchstens eine Übermittlung gleichzeitig). Bei 5xx oder Netzfehler automatisch erneut nach 2 s und
 *          noch einmal nach 6 s (Befund N4: App-Neustart beim Deploy → Proxy-Fehler 500); Duplikate fängt die App ab (gleicher
 *          Inhalt binnen 10 Minuten → keine zweite Anfrage, keine zweite Mail). Nutzer: assets/kult-b2b-form.js (<kult-b2b-form>),
 *          assets/kult-b2b-event-request.js (Team-Event-Anfrage), assets/kult-b2b-builder.js (Kontakt im Präsente-Konfigurator).
 *          Nach erfolgreicher Übermittlung (nicht bei Honigtopf-Treffern) feuert am Formular das Event „kult:b2b:sent“
 *          (bubbles, detail { interesse }) für assets/kult-analytics.js (29.10.2026: gezählt wird erst der Erfolg).
 *
 * Attribute (am Formular oder einem Elternelement, z. B. <kult-b2b-form>):
 *   data-lead-endpoint   URL (Standard /apps/seminare/b2b-anfrage)
 *   data-lead-interest   buerokaffee | praesente | catering | teamevent | allgemein | auto
 *   data-lead-fallback   Interesse bei "auto", wenn das Formular kein Feld "Interesse" hat (aus der Seite abgeleitet)
 *
 * Gesendeter JSON-Body (Header Content-Type und Accept application/json, x-requested-with kult-b2b):
 *   { formular, interesse, firma, name, email, telefon, nachricht, felder: { Label: Wert }, marketing, consentText, page, hp }
 *   formular   contact[Formular]            firma      contact[Firma]
 *   name       contact[Ansprechpartner], sonst contact[Name]
 *   email      contact[email]               telefon    contact[Telefon]
 *   nachricht  contact[Ihr Anschreiben], sonst contact[Nachricht], sonst contact[body]
 *   felder     alle übrigen contact[…]-Felder (leere Werte ausgelassen, Radio/Checkbox nur gewählt, höchstens 40 Einträge,
 *              Werte auf 500 Zeichen gekürzt; contact[body] heißt dort "Zusammenstellung", mehrzeilig, bis 4000 Zeichen;
 *              technische Felder contact[_…] wie _konfiguration (JSON) ungekürzt bis 4000 Zeichen)
 *   marketing  Häkchen contact[Firmenkunden-Angebote per E-Mail] gesetzt; consentText = sichtbarer Label-Text
 *   page       location.pathname            hp   Honigtopf-Feld name="hp" (die App verwirft Treffer still)
 * Interesse bei "auto": Feld contact[Interesse] ("Bürokaffee" -> buerokaffee, "Präsent" -> praesente,
 *   "Catering" -> catering, "Team"/"Event"/"Seminar" -> teamevent, sonst allgemein); ohne dieses Feld
 *   data-lead-fallback, dann contact[Formular] ("Bürokaffee" -> buerokaffee, "Präsent" -> praesente,
 *   "Event"/"Catering" -> catering), sonst allgemein.
 * Mehrfaches Laden des Skripts auf einer Seite ist unschädlich (Flag window.__kultB2bLead).
 */
(function () {
  'use strict';
  if (window.__kultB2bLead) return;
  window.__kultB2bLead = true;

  var DEFAULT_ENDPOINT = '/apps/seminare/b2b-anfrage';
  var TIMEOUT_MS = 20000;
  var RETRY_DELAYS_MS = [2000, 6000];
  var MSG_FAIL = 'Ihre Anfrage konnte gerade nicht gesendet werden. Bitte versuchen Sie es gleich noch einmal.';
  var CONSENT_NAME = 'contact[Firmenkunden-Angebote per E-Mail]';
  var INTERESTS = ['buerokaffee', 'praesente', 'catering', 'teamevent', 'allgemein'];
  var busy = new WeakMap();

  function clip(value, max) { return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, max); }
  function clipText(value, max) { return String(value == null ? '' : value).replace(/\r\n/g, '\n').trim().slice(0, max); }
  function has(text, needle) { return String(text || '').toLowerCase().indexOf(needle) !== -1; }

  // Attribut am Formular oder am nächsten Elternelement; null = nirgends gesetzt
  function attr(form, name) {
    var el = form.closest('[' + name + ']');
    return el ? el.getAttribute(name) : null;
  }

  function interestFromField(value) {
    if (has(value, 'bürokaffee') || has(value, 'buerokaffee')) return 'buerokaffee';
    if (has(value, 'präsent') || has(value, 'praesent')) return 'praesente';
    if (has(value, 'catering')) return 'catering';
    if (has(value, 'team') || has(value, 'event') || has(value, 'seminar')) return 'teamevent';
    return 'allgemein';
  }

  function interestFromFormName(value) {
    if (has(value, 'bürokaffee') || has(value, 'buerokaffee')) return 'buerokaffee';
    if (has(value, 'präsent') || has(value, 'praesent')) return 'praesente';
    if (has(value, 'event') || has(value, 'catering')) return 'catering';
    return 'allgemein';
  }

  // Sammelt alle contact[…]-Felder: { Label: Wert }, bei gleichem Namen (Checkbox-Gruppe) mit ", " verbunden
  function collect(form) {
    var values = {};
    var order = [];
    Array.prototype.forEach.call(form.elements, function (el) {
      var match = el.name && /^contact\[(.+)\]$/.exec(el.name);
      if (!match || el.disabled) return;
      var type = (el.type || '').toLowerCase();
      if (type === 'submit' || type === 'button' || type === 'reset' || type === 'file' || type === 'image') return;
      if ((type === 'checkbox' || type === 'radio') && !el.checked) return;
      var value = clipText(el.value, 4000);
      if (!value) return;
      var label = match[1];
      if (values[label] === undefined) { values[label] = value; order.push(label); }
      else values[label] += ', ' + value;
    });
    return { values: values, order: order };
  }

  function buildPayload(form) {
    var all = collect(form);
    var v = all.values;
    var used = { 'Formular': 1, 'Firma': 1, 'email': 1, 'Telefon': 1, 'tags': 1, 'Anrede': 1, 'Vorname': 1, 'Nachname': 1 };
    used[CONSENT_NAME.slice(8, -1)] = 1;
    // Sie-Form (15.10.2026): Anrede, Vorname und Nachname sind Pflicht — aber NUR, wenn das Formular die Felder hat.
    // Go-Live-Befund P0-2 (Cowork 15.10. abends): Formulare ohne die Felder (ältere Templates) schicken die Schlüssel
    // NICHT mit, sonst lehnt der Server jede Anfrage ab („neues Format“ = Schlüssel vorhanden).
    var hatNeueFelder = !!(form.elements['contact[Anrede]'] || form.elements['contact[Vorname]'] || form.elements['contact[Nachname]']);
    var anrede = v['Anrede'] || '';
    var vorname = v['Vorname'] || '';
    var nachname = v['Nachname'] || '';
    var name = (vorname + ' ' + nachname).replace(/\s+/g, ' ').trim();
    if (!name && v['Ansprechpartner']) { name = v['Ansprechpartner']; used['Ansprechpartner'] = 1; }
    else if (!name && v['Name']) { name = v['Name']; used['Name'] = 1; }
    var message = '';
    ['Ihr Anschreiben', 'Nachricht', 'body'].some(function (key) {
      if (!v[key]) return false;
      message = v[key]; used[key] = 1; return true;
    });

    var felder = {};
    var count = 0;
    all.order.forEach(function (label) {
      if (used[label] || count >= 40) return;
      var key = clip(label === 'body' ? 'Zusammenstellung' : label, 80);
      // Technische Felder mit „_“ (z. B. _konfiguration = JSON aus dem Präsente-Konfigurator) ungekürzt bis 4000 Zeichen, sonst bricht das JSON
      felder[key] = label === 'body' || label.charAt(0) === '_' ? clipText(v[label], 4000) : clip(v[label], 500);
      count += 1;
    });

    var checkbox = form.elements[CONSENT_NAME];
    if (checkbox && checkbox.length && !checkbox.tagName) checkbox = checkbox[0];
    var marketing = !!(checkbox && checkbox.checked);
    var consentText = '';
    if (checkbox) {
      var textEl = checkbox.closest('label') ? checkbox.closest('label').querySelector('.kult-b2b-consent__text') : null;
      var labelEl = textEl || checkbox.closest('label');
      consentText = clip(labelEl ? labelEl.textContent : '', 600);
    }

    var interest = clip(attr(form, 'data-lead-interest') || 'auto', 40).toLowerCase();
    if (interest === 'auto') {
      var fallback = clip(attr(form, 'data-lead-fallback'), 40).toLowerCase();
      if (v['Interesse']) interest = interestFromField(v['Interesse']);
      else if (INTERESTS.indexOf(fallback) !== -1 && fallback !== 'allgemein') interest = fallback;
      else interest = interestFromFormName(v['Formular']);
    }
    if (INTERESTS.indexOf(interest) === -1) interest = 'allgemein';

    var payload = {
      formular: clip(v['Formular'], 200),
      interesse: interest,
      firma: clip(v['Firma'], 120),
      name: clip(name, 120),
      email: clip(v['email'], 254),
      telefon: clip(v['Telefon'], 40),
      nachricht: clipText(message, 4000),
      felder: felder,
      marketing: marketing,
      consentText: consentText,
      page: location.pathname,
      hp: form.elements['hp'] ? String(form.elements['hp'].value || '') : ''
    };
    if (hatNeueFelder) {
      payload.anrede = clip(anrede, 10);
      payload.vorname = clip(vorname, 120);
      payload.nachname = clip(nachname, 120);
    }
    return payload;
  }

  // Ein Versuch; löst immer mit { ok, status, error, retry } auf. retry = 5xx oder Netzfehler/Zeitlimit (status 0).
  function attempt(endpoint, body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT_MS);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-requested-with': 'kult-b2b' },
      body: body,
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = JSON.parse(text); } catch (e) { data = null; }
        var ok = res.ok && !!data && data.ok !== false;
        return { ok: ok, status: res.status, error: ok ? '' : ((data && data.error) || MSG_FAIL), retry: !ok && res.status >= 500 };
      });
    }).catch(function () {
      return { ok: false, status: 0, error: MSG_FAIL, retry: true };
    }).then(function (result) {
      window.clearTimeout(timer);
      return result;
    });
  }

  function wait(ms) { return new Promise(function (resolve) { window.setTimeout(resolve, ms); }); }

  // Sendet die Anfrage (mit Wiederholungen, gleicher Body); löst immer mit { ok, status, error } auf, wirft nie.
  // Läuft schon eine Übermittlung für dieses Formular, kommt deren Ergebnis.
  function submit(form) {
    try {
      if (busy.has(form)) return busy.get(form);
      var endpoint = attr(form, 'data-lead-endpoint');
      if (endpoint === null || !String(endpoint).trim()) endpoint = DEFAULT_ENDPOINT;
      endpoint = String(endpoint).trim();
      var payload = buildPayload(form);
      var body = JSON.stringify(payload);
      var tries = 0;
      var run = function () {
        return attempt(endpoint, body).then(function (result) {
          if (result.retry && tries < RETRY_DELAYS_MS.length) {
            var delay = RETRY_DELAYS_MS[tries];
            tries += 1;
            return wait(delay).then(run);
          }
          return { ok: result.ok, status: result.status, error: result.error };
        });
      };
      var request = run().catch(function () {
        return { ok: false, status: 0, error: MSG_FAIL };
      }).then(function (result) {
        busy.delete(form);
        // Erfolgsmeldung für die Messung (kult-analytics.js): nur echte Übermittlungen, Honigtopf-Treffer nie
        if (result.ok && !payload.hp) {
          try { form.dispatchEvent(new CustomEvent('kult:b2b:sent', { bubbles: true, detail: { interesse: payload.interesse } })); } catch (e) { /* ignorieren */ }
        }
        return result;
      });
      busy.set(form, request);
      return request;
    } catch (err) {
      return Promise.resolve({ ok: false, status: 0, error: MSG_FAIL });
    }
  }

  window.KultB2BLead = { submit: submit, MSG_FAIL: MSG_FAIL };
})();
